import { CouponType as MonolithCouponType } from '@app/types/monolith/cart';
import { defineMessages, IntlShape } from '@redbubble/next/intl';
import { DeviceType } from '@redbubble/next/types';
import { StripeCart } from '../types';
import { getSubunitAmount } from '../utils';

type LineItem = {
  name: string;
  amount: number;
};

const messages = defineMessages({
  subtotalLabel: {
    defaultMessage:
      '{lineItemsCount} {lineItemsCount, plural, one {item} other {items}}',
  },
  subtotalWithCouponLabel: {
    defaultMessage:
      '{lineItemsCount} {lineItemsCount, plural, one {item} other {items}} (includes {couponDisplayName})',
  },
  taxLabel: {
    defaultMessage: 'Tax',
  },
  shippingLabel: {
    defaultMessage: 'Shipping',
  },
});

const isPercentCoupon = (couponType: MonolithCouponType) =>
  couponType === 'PercentOffCoupon' ||
  couponType === 'VariablePercentOffCoupon';

const isAmountCoupon = (couponType: MonolithCouponType) =>
  couponType === 'AmountCoupon' || couponType === 'GiftCertificateCoupon';

export const createCouponLine = (cart: StripeCart): LineItem | null => {
  const coupon = cart.appliedCoupon;

  // amount coupons reduce the overall amount payable, so we include these as a "negative amount" line item to ensure
  // the total amount balances as expected
  // e.g. { name: "US$10.00 discount", amount: -1000 }
  if (!coupon || !isAmountCoupon(coupon.type)) {
    return null;
  }

  const name = coupon.displayName;
  const couponAmount = coupon.discount;
  const amount = -1 * Math.floor(couponAmount * 100);

  return { name, amount };
};

export const createSubtotalLine = (
  cart: StripeCart,
  intl: IntlShape,
  isOrAbove: (deviceType: DeviceType) => boolean
): LineItem => {
  const amount = getSubunitAmount(cart.summary.subtotal);
  const { lineItemsCount } = cart.summary;

  // percent off coupons reduce individual item prices, but our subtotal aggregates this line item detail, so we include
  // some reassurance that the discount is included
  // e.g. { name: "5 items (includes 10% off any product)", amount: 864 }
  //
  // however, on Google Pay there appears to be a 16 character limit on the item name when viewing on mobile devices.
  // anything above this limit causes the order details to render completely blank. this is not documented anywhere,
  // but was discovered through trial and error. therefore we skip this extra discount info for mobile devices.
  const deviceTypeLargerThanMobile = isOrAbove(DeviceType.Tablet);
  const coupon = cart.appliedCoupon;
  if (deviceTypeLargerThanMobile && coupon && isPercentCoupon(coupon.type)) {
    const name = intl.formatMessage(messages.subtotalWithCouponLabel, {
      lineItemsCount,
      couponDisplayName: coupon.displayName,
    });
    return { name, amount };
  }

  const name = intl.formatMessage(messages.subtotalLabel, {
    lineItemsCount,
  });
  return { name, amount };
};

export const createTaxLine = (
  cart: StripeCart,
  intl: IntlShape
): LineItem | null => {
  const { tax, showTaxSeparately } = cart.summary;

  if (!tax || !showTaxSeparately) {
    return null;
  }

  const name = intl.formatMessage(messages.taxLabel);
  const amount = getSubunitAmount(tax.amount);

  return { name, amount };
};

export const createShippingLine = (
  cart: StripeCart,
  intl: IntlShape
): LineItem => {
  // this is intentionally the undiscounted shipping amount so that the customer has visibility on how much each
  // shipping option will consume of any coupons / gift certificates they have applied to their cart

  const name = intl.formatMessage(messages.shippingLabel);
  const { showTaxSeparately } = cart.summary;
  const estimatedShipping = cart.summary.estimatedShipping;
  const shippingPreference =
    estimatedShipping.preference?.toLowerCase() || 'standard';
  const shippingAmountWithTax = estimatedShipping[shippingPreference] || 0;
  const shippingAmountExcludingTax =
    estimatedShipping[`${shippingPreference}ExTax`] || 0;
  const shippingAmount = showTaxSeparately
    ? shippingAmountExcludingTax
    : shippingAmountWithTax;
  const amount = getSubunitAmount(shippingAmount);

  return { name, amount };
};

export const fromCart = (
  cart: StripeCart,
  intl: IntlShape,
  isOrAbove: (deviceType: DeviceType) => boolean
): LineItem[] => {
  const items: LineItem[] = [];

  items.push(createSubtotalLine(cart, intl, isOrAbove));
  items.push(createShippingLine(cart, intl));

  const taxLine = createTaxLine(cart, intl);
  if (taxLine) {
    items.push(taxLine);
  }

  const couponLine = createCouponLine(cart);
  if (couponLine) {
    items.push(couponLine);
  }

  return items;
};
